import React from 'react';
import CaseActionDialog from './CaseActionDialog';
import { useTranslation } from 'react-i18next';

/**
 * CaseRenameDialog Component
 * 
 * A specialized dialog component for renaming case titles.
 * This component provides a focused interface for case renaming operations.
 * 
 * Features:
 * - Dedicated interface for renaming cases
 * - Input validation (prevents empty names)
 * - Keyboard support (Enter to submit)
 * - Internationalization support
 * - Consistent styling with the application theme
 * - Automatic focus on input field when opened
 */
const CaseRenameDialog = ({
  open,
  onClose,
  currentTitle = '',
  onRename,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0'
}) => {
  const { t } = useTranslation();
  const [renameValue, setRenameValue] = React.useState(currentTitle);

  // Update the input value when the dialog opens with a new case
  React.useEffect(() => {
    if (open && currentTitle) {
      setRenameValue(currentTitle);
    }
  }, [open, currentTitle]);

  const handleSubmit = () => {
    const trimmedName = renameValue.trim();
    
    if (!trimmedName) {
      // Don't submit if name is empty - the dialog will handle validation
      return;
    }

    if (trimmedName === currentTitle) {
      // No change needed, just close
      handleClose();
      return;
    }

    onRename(trimmedName);
    handleClose();
  };

  const handleClose = () => {
    setRenameValue('');
    onClose();
  };

  return (
    <CaseActionDialog
      open={open}
      onClose={handleClose}
      value={renameValue}
      onValueChange={setRenameValue}
      onSubmit={handleSubmit}
      type="rename"
      primaryColor={primaryColor}
      hoverColor={hoverColor}
      title={t('sidebar.rename_case') || 'Rename Case'}
      placeholder={t('sidebar.case_name') || 'Case Name'}
      submitButtonText={t('sidebar.rename_case') || 'Rename'}
    />
  );
};

export default CaseRenameDialog;
